import { canonicalKeyboardTypeForRhythmPolicy } from "./globalRhythmKeyboardPolicy";
import {
  is24GWorkMode,
  resolveEquipmentWorkMode,
} from "./hfdWirelessMusicRhythm";
import { normalizeEquipmentName } from "./supportedKeyboardMatch";
import { isBeiyingDevice8815X98Keyboard } from "../main/beiyingX981K";

const BEIYING_ES98_WIRED_PRODUCT_ID = 0x10b7;
const BEIYING_ES98_24G_PRODUCT_ID = 0x106c;

export function isBeiyingEs98RhythmFamily(
  equipment: Record<string, unknown>
): boolean {
  const kt = canonicalKeyboardTypeForRhythmPolicy(equipment.keyboardType);
  if (kt === "99") return true;
  return (
    normalizeEquipmentName(
      equipment.displayname ?? equipment.displayName ?? equipment.productName
    ) === "es98"
  );
}

export function isBeiyingEs9824GInterfaceRhythmTarget(
  equipment: Record<string, unknown>,
  hidDevice?: unknown
): boolean {
  if (!isBeiyingEs98RhythmFamily(equipment)) return false;
  if (equipment.is4G === true) return true;
  const pid = Number(
    (hidDevice as { productId?: unknown } | undefined)?.productId ??
      equipment.productId
  );
  if (pid === BEIYING_ES98_24G_PRODUCT_ID) return true;
  const pn = String(
    (hidDevice as { productName?: unknown } | undefined)?.productName ??
      equipment.productName ??
      ""
  )
    .trim()
    .toLowerCase();
  if (/wireless receiver|8k wireless/.test(pn)) return true;
  return is24GWorkMode(resolveEquipmentWorkMode(equipment));
}

function normalizeBeiyingRhythmFamilyIdent(
  equipment: Record<string, unknown> | null | undefined
): string {
  if (!equipment || typeof equipment !== "object") return "";
  const raw =
    equipment.uuid ??
    equipment.identifier ??
    equipment.deviceId ??
    equipment.stableId;
  if (Array.isArray(raw)) {
    return raw.map((x) => String(x).trim().toLowerCase()).join(",");
  }
  return String(raw ?? "")
    .trim()
    .toLowerCase();
}

/** X98 8K web-hid（keyboardType 98，非 8815 X98_1K） */
export function isBeiyingX988KRhythmFamily(
  equipment: Record<string, unknown>
): boolean {
  if (isBeiyingDevice8815X98Keyboard(equipment)) return false;
  const kt = canonicalKeyboardTypeForRhythmPolicy(equipment.keyboardType);
  if (kt === "98") return true;
  const name = normalizeEquipmentName(
    equipment.displayname ?? equipment.displayName ?? equipment.productName
  );
  return name === "x98";
}

export function isBeiyingX988K24GInterfaceRhythmTarget(
  equipment: Record<string, unknown>,
  hidDevice?: unknown
): boolean {
  if (!isBeiyingX988KRhythmFamily(equipment)) return false;
  if (equipment.is4G === true) return true;
  const pid = Number(
    (hidDevice as { productId?: unknown } | undefined)?.productId ??
      equipment.productId
  );
  if (pid === BEIYING_ES98_24G_PRODUCT_ID) return true;
  const pn = String(
    (hidDevice as { productName?: unknown } | undefined)?.productName ??
      equipment.productName ??
      ""
  )
    .trim()
    .toLowerCase();
  if (/wireless receiver|8k wireless/.test(pn)) return true;
  return is24GWorkMode(resolveEquipmentWorkMode(equipment));
}

/** X98 8K 有线(0x10b0) / 2.4G(0x106c) 为同一物理键盘，快照恢复时按 uuid + keyboardType 对齐 */
export function isBeiyingX988KFamilySamePhysicalKeyboard(
  left: Record<string, unknown> | null | undefined,
  right: Record<string, unknown> | null | undefined
): boolean {
  if (!left || !right) return false;
  if (!isBeiyingX988KRhythmFamily(left) || !isBeiyingX988KRhythmFamily(right)) {
    return false;
  }

  const lSolution = String(left.solutionRovider ?? "")
    .trim()
    .toLowerCase();
  const rSolution = String(right.solutionRovider ?? "")
    .trim()
    .toLowerCase();
  if (lSolution && rSolution && lSolution !== rSolution) return false;

  const lKt = canonicalKeyboardTypeForRhythmPolicy(left.keyboardType);
  const rKt = canonicalKeyboardTypeForRhythmPolicy(right.keyboardType);
  if (lKt && rKt && lKt !== rKt) return false;

  const lIdent = normalizeBeiyingRhythmFamilyIdent(left);
  const rIdent = normalizeBeiyingRhythmFamilyIdent(right);
  if (lIdent && rIdent) return lIdent === rIdent;

  return true;
}

/**
 * 中颖方案 X98 8K（与贝盈原生 0x372e X98 8K 同为 keyboardType=98，但 VID/PID 不同）。
 * - 有线：0x391d / 0x200b
 * - 2.4G：0x258a / 0x02d7
 */
export function isBeiyingZhongyingX988KKeyboard(
  equipment: Record<string, unknown> | null | undefined
): boolean {
  if (!equipment) return false;
  const vid = Number(equipment.vendorId);
  const pid = Number(equipment.productId);
  if (!Number.isFinite(vid) || !Number.isFinite(pid)) return false;
  if (vid === 0x391d && pid === 0x200b) return true;
  if (vid === 0x258a && pid === 0x02d7) return true;
  return false;
}

/** ES98 / X98 8K 等 web-hid 贝盈：有线与 2.4G PID 不同但 uuid 相同 */
export function isBeiyingWebHidCrossInterfaceRhythmFamily(
  equipment: Record<string, unknown>
): boolean {
  return (
    isBeiyingEs98RhythmFamily(equipment) ||
    isBeiyingX988KRhythmFamily(equipment)
  );
}

export function isBeiyingWebHidCrossInterfaceSamePhysicalKeyboard(
  left: Record<string, unknown> | null | undefined,
  right: Record<string, unknown> | null | undefined
): boolean {
  return (
    isBeiyingEs98FamilySamePhysicalKeyboard(left, right) ||
    isBeiyingX988KFamilySamePhysicalKeyboard(left, right)
  );
}

export function shouldForceBeiyingWebHidRoutineSnapshotCapture(
  equipment: Record<string, unknown>
): boolean {
  return isBeiyingWebHidCrossInterfaceRhythmFamily(equipment);
}

function normalizeBeiyingEs98FamilyIdent(
  equipment: Record<string, unknown> | null | undefined
): string {
  return normalizeBeiyingRhythmFamilyIdent(equipment);
}

/** ES98 有线(0x10b7) / 2.4G(0x106c) 为同一物理键盘，快照恢复时按 uuid + keyboardType 对齐 */
export function isBeiyingEs98FamilySamePhysicalKeyboard(
  left: Record<string, unknown> | null | undefined,
  right: Record<string, unknown> | null | undefined
): boolean {
  if (!left || !right) return false;
  if (!isBeiyingEs98RhythmFamily(left) || !isBeiyingEs98RhythmFamily(right)) {
    return false;
  }

  const lSolution = String(left.solutionRovider ?? "")
    .trim()
    .toLowerCase();
  const rSolution = String(right.solutionRovider ?? "")
    .trim()
    .toLowerCase();
  if (lSolution && rSolution && lSolution !== rSolution) return false;
  if (lSolution !== "beiying" && rSolution !== "beiying") {
    if (lSolution || rSolution) return false;
  }

  const lKt = canonicalKeyboardTypeForRhythmPolicy(left.keyboardType);
  const rKt = canonicalKeyboardTypeForRhythmPolicy(right.keyboardType);
  if (lKt && rKt && lKt !== rKt) return false;

  const lIdent = normalizeBeiyingEs98FamilyIdent(left);
  const rIdent = normalizeBeiyingEs98FamilyIdent(right);
  if (lIdent && rIdent) return lIdent === rIdent;

  return true;
}

export function equipmentFromBeiyingRoutineSnapshotMeta(
  routineBase: Record<string, unknown>,
  fallbackSolution = "beiying"
): Record<string, unknown> {
  return {
    solutionRovider: routineBase.__solutionRovider ?? fallbackSolution,
    keyboardType: routineBase.__keyboardType,
    vendorId: routineBase.__vendorId ?? routineBase.vendorId,
    productId: routineBase.__productId ?? routineBase.productId,
    uuid: routineBase.__identifier,
    identifier: routineBase.__identifier,
  };
}

export function isBeiyingEs98WiredInterfaceRhythmTarget(
  equipment: Record<string, unknown>,
  hidDevice?: unknown
): boolean {
  if (!isBeiyingEs98RhythmFamily(equipment)) return false;
  if (isBeiyingEs9824GInterfaceRhythmTarget(equipment, hidDevice)) {
    return false;
  }
  const pid = Number(
    (hidDevice as { productId?: unknown } | undefined)?.productId ??
      equipment.productId
  );
  if (pid === BEIYING_ES98_WIRED_PRODUCT_ID) return true;
  const wm = resolveEquipmentWorkMode(equipment);
  return wm === 0 || wm === "0";
}

/**
 * 全局律动：未连 ES98 2.4G 时隐藏接收器幽灵项；当前为有线 ES98 时隐藏 2.4G 行。
 */
export function shouldShowBeiyingRhythmTargetForActiveConnection(
  targetEquipment: Record<string, unknown>,
  activeEquipment: Record<string, unknown> | null | undefined,
  hidDevice?: unknown
): boolean {
  if (String(targetEquipment.solutionRovider ?? "") !== "beiying") {
    return true;
  }
  if (!isBeiyingEs98RhythmFamily(targetEquipment)) return true;

  const target24 = isBeiyingEs9824GInterfaceRhythmTarget(
    targetEquipment,
    hidDevice
  );

  if (!activeEquipment || !isBeiyingEs98RhythmFamily(activeEquipment)) {
    return !target24;
  }

  const activeOn24 = is24GWorkMode(resolveEquipmentWorkMode(activeEquipment));
  if (activeOn24) return target24;
  return !target24;
}

/** 贝盈律动/壁纸 RGB 下发：当前会话是否为 2.4G（ES98 / X98 8K 等） */
export function isBeiyingRhythmTargetOn24G(
  equipment: Record<string, unknown>
): boolean {
  if (equipment.is4G === true) return true;
  if (is24GWorkMode(resolveEquipmentWorkMode(equipment))) return true;
  if (isBeiyingEs9824GInterfaceRhythmTarget(equipment)) return true;
  if (isBeiyingX988K24GInterfaceRhythmTarget(equipment)) return true;
  return false;
}

/**
 * 与连接页 `beiying_is2_4G` 对齐；全局律动 apply 设备后须同步，否则误走 updateRGB 导致 2.4G 卡顿。
 */
export function syncBeiyingIs2_4GLocalStorageFlag(
  equipment: Record<string, unknown> | null | undefined
): boolean {
  if (!equipment) {
    return resolveBeiyingRhythmSendUsesWirelessRgb(null);
  }
  const on24G = isBeiyingRhythmTargetOn24G(equipment);
  if (typeof window === "undefined") return on24G;
  try {
    if (on24G) {
      window.localStorage.setItem("beiying_is2_4G", "true");
    } else {
      window.localStorage.removeItem("beiying_is2_4G");
    }
  } catch {
    // ignore
  }
  return on24G;
}

/** 从 localStorage 设备 + beiying_is2_4G 回退判断 2.4G 会话 */
export function resolveBeiyingRhythmSendUsesWirelessRgb(
  equipment: Record<string, unknown> | null | undefined
): boolean {
  if (equipment && isBeiyingRhythmTargetOn24G(equipment)) return true;
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem("beiying_is2_4G") === "true";
  } catch {
    return false;
  }
}
